<script setup>
import { VForm } from 'vuetify/components/VForm';
import { VTextField } from 'vuetify/lib/components/index.mjs';
import { useStore } from 'vuex';
definePage({ meta: { layout: 'login' } });

const refVForm = ref();
const store = useStore();
const route = useRoute();
const router = useRouter();

const show1 = ref(false);

const rules1 = {
  required: (value) => !!value || '아이디를 입력해주세요.',
  //min: (v) => v.length >= 4 || '4자리 이상 ',
};
const rules2 = {
  required: (value) => !!value || '비밀번호를 입력해주세요.',
  //min: (v) => v.length >= 4 || '4자리 이상 ',
};
const credentials = ref({
  loginId: '',
  loginPw: '',
});

const errors = ref({
  loginId: undefined,
  loginPw: undefined,
});

// partner/smartwel 연동 흐름에서 진입한 경우: 가입 분기로 빠져도 가입 완료 후 partner 페이지로 복귀하도록
// returnPath를 sessionStorage에 TTL과 함께 보관 (login.vue 자체는 to query로 처리, 가입 흐름은 헬퍼로 소비).
import { setReturnPath } from '@/composables/usePartnerLinkReturn';
import smartwelLogo from '@/assets/partner/smartwel/smartwel-wordmark__black.png';
import { getLastLoginHint, setPendingSocialType } from '@/utils/lastLoginHint';

const isPartnerSmartwel = ref(false);
const recentSocialType = ref(null);

// 구글 OAuth는 앱 WebView에서 차단(disallowed_useragent)되어 앱은 네이티브 로그인을 사용한다.
// 따라서 구글 버튼은 뷰포트 폭이 아니라 '앱 WebView 여부'(isApp)로만 숨긴다.
// (모바일 웹/PC 웹에서는 정상 노출)
const { isApp } = useClientType();

const snsButtons = [
  { key: 'kakao', label: '카카오', iconClass: 'icon-kakao' },
  { key: 'naver', label: '네이버', iconClass: 'icon-naver' },
  { key: 'google', label: '구글', iconClass: 'icon-google' },
  { key: 'apple', label: '애플', iconClass: 'icon-apple' },
];

// 앱 WebView에서는 구글 제외
const visibleSnsButtons = computed(() =>
  snsButtons.filter((item) => !(item.key === 'google' && isApp.value)),
);

onMounted(() => {
  const to = route.query.to;
  if (typeof to === 'string' && /\/partner\/smartwel\?/.test(to)) {
    isPartnerSmartwel.value = true;
    if (/autoLink=1/.test(to)) {
      setReturnPath(to);
    }
  }

  const hint = getLastLoginHint();
  if (hint?.loginType === 'social' && hint.socialType) {
    recentSocialType.value = hint.socialType;
  }
});

const onSubmit = () => {
  refVForm.value?.validate().then(({ valid: isValid }) => {
    if (isValid) login();
  });
};
const login = async () => {
  const result = await store.dispatch('user/login', {
    loginId: credentials.value.loginId,
    loginPw: credentials.value.loginPw,
  });
  if (store.state.user.errorMsg) {
    alert(store.state.user.errorMsg);
    return;
  }

  await nextTick(() => {
    const toPath = route.query.to ? String(route.query.to) : '/main';
    let redirectPath = toPath;
    const query = {};

    // toPath에 쿼리 파라미터가 포함되어 있다면 분리
    if (toPath.includes('?')) {
      const [path, queryString] = toPath.split('?');
      redirectPath = path;
      const searchParams = new URLSearchParams(queryString);
      for (const [key, value] of searchParams) {
        query[key] = value;
      }
    }

    const redirectOption = route.query?.redirectOption;

    if (redirectPath === '/saju-input') {
      query.loadTempFormData = true;
    }
    if (redirectOption) {
      // 사주 퀵 메뉴 팝업에서 로그인 후 이동 옵션
      query.redirectOption = redirectOption;
    }
    if (route.query.toQuery) {
      // 1:1 문의 / 이벤트 신청에서 로그인 후 원래 쿼리 유지
      try {
        const toQuery = JSON.parse(String(route.query.toQuery));
        Object.assign(query, toQuery);
      } catch (e) {
        console.error('Failed to parse toQuery:', e);
      }
    }

    router.replace({
      path: redirectPath,
      query,
    });
  });
};

const isSignInDisabled = computed(() => {
  return credentials.value.loginId.length < 3 || credentials.value.loginPw.length < 3;
});

const SNSlogin_blank = (provider) => {
  // 콜백 시점에는 provider 정보를 알 수 없으므로 시작 시점에 보관 → callback에서 hint로 승격
  setPendingSocialType(provider);
  const apiURL = import.meta.env.VITE_APP_GW_URL;
  window.open(`${apiURL}/oauth/login/${provider}`, '_self');
};
</script>

<template>
  <VRow no-gutters class="login-page">
    <VCol cols="12">
      <VCard flat class="mt-sm-0 pa-0 login-inner__card">
        <div class="login-logo-group" :class="{ 'login-logo-group--partner': isPartnerSmartwel }">
          <RouterLink to="/main" class="login-logo__box">
            <div class="login-logo" />
          </RouterLink>
          <template v-if="isPartnerSmartwel">
            <span class="login-logo-group__x" aria-hidden="true">×</span>
            <img :src="smartwelLogo" alt="복지찬스" class="login-logo-group__partner" />
          </template>
        </div>

        <VCardText>
          <VForm ref="refVForm" @submit.prevent="onSubmit">
            <VRow no-gutters class="login-form">
              <VCol cols="12" class="login-form__input-box">
                <VTextField
                  v-model="credentials.loginId"
                  autofocus
                  placeholder="아이디"
                  type="text"
                  autocapitalize="none"
                  autocorrect="off"
                  spellcheck="false"
                  :rules="[rules1.required]"
                  :error-messages="errors.loginId"
                />
                <VTextField
                  v-model="credentials.loginPw"
                  placeholder="비밀번호"
                  :rules="[rules2.required]"
                  :type="show1 ? 'text' : 'password'"
                  :error-messages="errors.password"
                  :append-inner-icon="show1 ? 'ri-eye-off-line' : 'ri-eye-line'"
                  @click:append-inner="show1 = !show1"
                />
              </VCol>

              <VCol cols="12" class="login-form__button-box">
                <VBtn block :disabled="isSignInDisabled" type="submit" class="h-12 login-button">
                  로그인
                </VBtn>
                <div class="join-button"><a href="/auth/join">일반 회원가입</a></div>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>

        <VCol cols="12" class="auth-providers">
          <div class="find-box">
            <button class="btn-nor" @click="() => router.push('/auth/find-id')">아이디 찾기</button>
            <button class="btn-nor" @click="() => router.push('/auth/reset')">
              비밀번호 재설정
            </button>
          </div>

          <div class="sns-box">
            <div
              v-for="item in visibleSnsButtons"
              :key="item.key"
              class="sns-item"
            >
              <div v-if="recentSocialType === item.key" class="sns-recent-badge">
                최근 로그인
                <span class="sns-recent-badge__tail" />
              </div>
              <button
                class="sns-button"
                :class="item.iconClass"
                @click="SNSlogin_blank(item.key)"
              >
                {{ item.label }}
              </button>
            </div>
          </div>
        </VCol>
      </VCard>
    </VCol>

  </VRow>
</template>

<style scoped>
/* partner 진입 시 듀얼 로고 한 영역으로 묶기 */
.login-logo-group--partner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.login-logo-group__x {
  font-size: 16px;
  color: #c5bdb0;
  font-weight: 300;
  line-height: 1;
}

.login-logo-group__partner {
  height: 30px;
  width: auto;
  display: block;
}

/* 최근 로그인 SNS 버튼 위 말풍선 (absolute로 띄워 행 정렬에 영향 X) */
.sns-item {
  position: relative;
}

.sns-recent-badge {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background-color: #eb4c10;
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  padding: 4px 8px;
  border-radius: 11px;
  white-space: nowrap;
  pointer-events: none;
}

.sns-recent-badge__tail {
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #eb4c10;
}
</style>
